import type {
  PlanDraft,
  PlanLegDraft,
  PlanRowDraft,
} from '@/pages/route-planner/components/routePlanDraft';

import type { RouteDraft, RouteLegDraft, RouteRowDraft } from './ujpWizard';

/**
 * Adapter between the wizard's `RouteDraft` and the Route Planner's `PlanDraft`
 * (R3, D4b). The two models are near-identical — same stops+legs shape — but
 * differ in leg provenance (`RouteLegDraft.manual` ↔ `PlanLegDraft.edited`) and
 * in optionality (`RouteRowDraft.addressId/endpoint` and `RouteLegDraft.source`
 * are optional on the wizard side, required on the plan side). This lets the
 * wizard reuse `RoutePlanBuilder` inline for manual route editing without a
 * second stop-editor. The lane linkage (`addressId`/`endpoint`) and the leg
 * `source` badge round-trip losslessly; only the UJP wire payload drops them.
 *
 * A TODO tracks unifying the two draft models so this adapter can go away.
 */

const stopToPlan = (s: RouteRowDraft): PlanRowDraft => ({
  uid: s.uid,
  type: s.type,
  intent: s.intent,
  pool: s.pool,
  charged: s.charged,
  name: s.name,
  address: s.address,
  latitude: s.latitude,
  longitude: s.longitude,
  addressId: s.addressId ?? null,
  endpoint: s.endpoint ?? null,
  workflowId: s.workflowId ?? null,
  workflowName: s.workflowName ?? null,
});

const legToPlan = (l: RouteLegDraft): PlanLegDraft => ({
  fromUid: l.fromUid,
  toUid: l.toUid,
  km: l.km,
  source: l.source ?? null,
  edited: l.manual,
});

const stopToRoute = (s: PlanRowDraft): RouteRowDraft => ({
  uid: s.uid,
  type: s.type,
  intent: s.intent,
  pool: s.pool,
  charged: s.charged,
  name: s.name,
  address: s.address,
  latitude: s.latitude,
  longitude: s.longitude,
  addressId: s.addressId,
  endpoint: s.endpoint,
  workflowId: s.workflowId ?? null,
  workflowName: s.workflowName ?? null,
});

const legToRoute = (l: PlanLegDraft): RouteLegDraft => ({
  fromUid: l.fromUid,
  toUid: l.toUid,
  km: l.km,
  manual: l.edited,
  source: l.source,
});

/** `RouteDraft` → `PlanDraft` (feed the inline `RoutePlanBuilder`). */
export const toPlanDraft = (route: RouteDraft): PlanDraft => ({
  stops: route.stops.map(stopToPlan),
  legs: route.legs.map(legToPlan),
});

/** `PlanDraft` → `RouteDraft` (store the builder's output on the wizard form). */
export const toRouteDraft = (plan: PlanDraft): RouteDraft => ({
  stops: plan.stops.map(stopToRoute),
  legs: plan.legs.map(legToRoute),
});
